import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { isoDateStringToUSFormat } from '@/util/time'
import { MedChronEvent, UIMedChronEvent, UIEventFieldItem, EventFieldItem } from './schemas'

/**
 * Select: Med Chron Events List by Store Key
 *
 * Returns the list of medical chronology events for the given store key, or null if it does not exist.
 *
 * @returns
 * - `null` - Data has not been loaded yet (key doesn't exist in store)
 * - `[]` - Data was loaded but the chronology has no events (empty array)
 * - `MedChronEvent[]` - Data was loaded and contains events
 */
export const selectMedChronEventsListByStoreKey = createSelector(
  [(state: RootState) => state.medChronEventsState.events, (_: RootState, props: { storeKey: string | undefined | null }) => props.storeKey],
  (events, storeKey): MedChronEvent[] | null => {
    if (!storeKey) return null
    // Return null if key doesn't exist (not loaded), otherwise return the array (could be empty or have data)
    return events[storeKey] ?? null
  }
)

/**
 * Helper: Convert EventFieldItem to UIEventFieldItem
 */
function toUIEventFieldItem<T>(item: EventFieldItem<T>, transformValue: (value: T) => string): UIEventFieldItem {
  return {
    reference_id: item.reference_id,
    text: transformValue(item.value),
  }
}

/**
 * Helper: Process ICD-10 codes by trimming off diagnosis text
 * TODO: this can be removed once we sort out old cached data, new cached data will not include the extra text
 */
function processICD10Code(icd10String: string): string {
  return icd10String.split(' -')[0] || icd10String
}

/**
 * Select: UI Med Chron Events List by Store Key
 *
 * Returns the list of medical chronology events transformed to UI-ready format with all preprocessing applied:
 * - Dates formatted as strings
 * - All values converted to text strings
 * - ICD-10 codes trimmed (diagnosis text removed)
 *
 * @returns
 * - `null` - Data has not been loaded yet (key doesn't exist in store)
 * - `[]` - Data was loaded but the chronology has no events (empty array)
 * - `UIMedChronEvent[]` - Data was loaded and contains preprocessed events
 */
export const selectUIMedChronEventsListByStoreKey = createSelector([selectMedChronEventsListByStoreKey], (eventsList): UIMedChronEvent[] | null => {
  if (eventsList === null) return null

  return eventsList.map((event): UIMedChronEvent => {
    return {
      id: event.id,
      dateOfService: toUIEventFieldItem(event.date_of_service, (isoDateString) => isoDateStringToUSFormat(isoDateString)),
      facility: toUIEventFieldItem(event.facility, (value) => value),
      // There will only ever be one provider for now, the event schema defines it as a list for future extensibility
      provider: toUIEventFieldItem(event.providers[0], (value) => value),
      summarySentences: event.summary_sentences.map((item) => toUIEventFieldItem(item, (value) => value)),
      diagnoses: event.diagnoses.map((item) => toUIEventFieldItem(item, (value) => value)),
      icd10Codes: event.icd_10_codes.map((item) => toUIEventFieldItem(item, (value) => processICD10Code(value))),
      testsAndOrImaging: event.tests_and_or_imaging.map((item) => toUIEventFieldItem(item, (value) => value)),
      procedures: event.procedures.map((item) => toUIEventFieldItem(item, (value) => value)),
      medications: event.medications.map((item) => toUIEventFieldItem(item, (value) => value)),
      source: event.source,
      pages: event.pages,
    }
  })
})
